import { BlobSinkAction } from '@/services/types/blob-types';

interface SmartAdvocateTargetMetadata {
  document_direction_id?: number | null;
  category_name?: string | null;
  subcategory_name?: string | null;
  from_contact_name?: string | null;
  from_unique_contact_id?: number | null;
  to_contact_name?: string | null;
  to_unique_contact_id?: number | null;
  description?: string | null;
}

interface NeosTargetMetadata {
  doc_type_title?: string | null;
  document_category_title?: string | null;
}

export interface SinkMetadataDisplayRow {
  label: string;
  value: string;
}

function formatDirection(directionId: number | null | undefined): string | null {
  if (directionId === 1) {
    return 'Incoming';
  }
  if (directionId === 2) {
    return 'Outgoing';
  }
  return null;
}

function formatContact(
  name: string | null | undefined,
  id: number | null | undefined,
): string | null {
  if (name && name.trim().length > 0) {
    return name.trim();
  }
  if (id != null) {
    return `Contact #${id}`;
  }
  return null;
}

function getSmartAdvocateRows(
  metadata: SmartAdvocateTargetMetadata,
): SinkMetadataDisplayRow[] {
  const rows: SinkMetadataDisplayRow[] = [];

  const direction = formatDirection(metadata.document_direction_id);
  if (direction) {
    rows.push({ label: 'Direction', value: direction });
  }

  const categoryParts = [metadata.category_name, metadata.subcategory_name].filter(
    (part): part is string => !!part && part.trim().length > 0,
  );
  if (categoryParts.length > 0) {
    rows.push({ label: 'Category', value: categoryParts.join(' / ') });
  }

  const fromContact = formatContact(
    metadata.from_contact_name,
    metadata.from_unique_contact_id,
  );
  if (fromContact) {
    rows.push({ label: 'From', value: fromContact });
  }

  const toContact = formatContact(
    metadata.to_contact_name,
    metadata.to_unique_contact_id,
  );
  if (toContact) {
    rows.push({ label: 'To', value: toContact });
  }

  if (metadata.description && metadata.description.trim().length > 0) {
    rows.push({ label: 'Description', value: metadata.description.trim() });
  }

  return rows;
}

function getNeosRows(metadata: NeosTargetMetadata): SinkMetadataDisplayRow[] {
  const rows: SinkMetadataDisplayRow[] = [];

  if (metadata.doc_type_title && metadata.doc_type_title.trim().length > 0) {
    rows.push({ label: 'Document Type', value: metadata.doc_type_title.trim() });
  }

  if (
    metadata.document_category_title &&
    metadata.document_category_title.trim().length > 0
  ) {
    rows.push({ label: 'Category', value: metadata.document_category_title.trim() });
  }

  return rows;
}

export function getSinkMetadataDisplayRows(
  sinkAction: BlobSinkAction,
): SinkMetadataDisplayRow[] {
  const system = sinkAction.target_system.toUpperCase();

  if (!sinkAction.target_metadata) {
    return [];
  }

  if (system === 'SMARTADVOCATE') {
    return getSmartAdvocateRows(
      sinkAction.target_metadata as SmartAdvocateTargetMetadata,
    );
  }

  if (system === 'NEOS') {
    return getNeosRows(sinkAction.target_metadata as NeosTargetMetadata);
  }

  return [];
}
